import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle2, ExternalLink, Loader2, AlertTriangle, Clock, CreditCard } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { toastError } from '../../services/errorUtils'

interface Plan {
  id: string
  name: string
  price_cents: number | null
  price_formatted: string
  currency: string
  interval: string
  limits: Record<string, unknown>
}

interface Subscription {
  id: string
  status: string
  plan_code: string
  current_period_start: string | null
  current_period_end: string | null
  period_days: number | null
  cancel_at_period_end: boolean
  days_remaining: number | null
  checkout_url?: string | null
}

interface CheckStatusData {
  subscription: Subscription | null
  plans: Plan[]
}

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  active: { label: 'Ativo', className: 'badge-success' },
  pending_payment: { label: 'Pagamento Pendente', className: 'badge-warning' },
  expired: { label: 'Expirado', className: 'badge-danger' },
  canceled: { label: 'Cancelado', className: 'badge-danger' },
  failed: { label: 'Falhou', className: 'badge-danger' },
  dry_run: { label: 'Simulação', className: 'badge-info' },
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '—'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date(dateStr))
}

export default function BillingPage() {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<CheckStatusData | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)

  useEffect(() => {
    const checkoutResult = searchParams.get('checkout')
    if (checkoutResult === 'success') toast.success('Pagamento confirmado!')
    else if (checkoutResult === 'return') toast('Retornou do checkout.', { icon: '🔄' })
  }, [searchParams])

  useEffect(() => {
    apiService.getBillingCheckStatus()
      .then((res) => setData(res.data?.data || null))
      .catch((error) => toastError(error, 'Erro ao carregar faturamento'))
      .finally(() => setLoading(false))
  }, [])

  async function handleCheckout(planId: string) {
    setCheckoutLoading(planId)
    try {
      const returnUrl = `${window.location.origin}/app/billing?checkout=return`
      const completionUrl = `${window.location.origin}/app/billing?checkout=success`
      const res = await apiService.createBillingCheckout({ plan_id: planId, return_url: returnUrl, completion_url: completionUrl })
      const checkoutUrl = res.data?.data?.checkout?.url
      if (checkoutUrl) window.open(checkoutUrl, '_blank')
      else toast.success('Checkout gerado (dry-run)')
      toast.success('Assinatura criada!')
    } catch (error) {
      toastError(error, 'Erro ao gerar checkout')
    } finally {
      setCheckoutLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  const sub = data?.subscription
  const statusInfo = sub ? STATUS_MAP[sub.status] || { label: sub.status, className: 'badge-info' } : null

  return (
    <div className="mx-auto max-w-5xl space-y-8 py-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Planos e assinatura</h1>
        <p className="mt-1 text-sm text-slate-500">Escolha o plano ideal para sua clínica.</p>
      </div>

      {/* Current subscription */}
      {sub && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Sua assinatura</h2>
              <p className="mt-1 text-sm text-slate-500">
                Plano {sub.plan_code} &middot; <span className={statusInfo?.className}>{statusInfo?.label}</span>
              </p>
            </div>
            {sub.status === 'pending_payment' && (
              <div className="flex items-center gap-2 rounded-lg bg-warning-50 px-4 py-2 text-sm text-warning-800">
                <Clock className="h-4 w-4" />
                Pagamento pendente — aguardando confirmação
              </div>
            )}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-slate-50 px-4 py-3">
              <p className="text-xs text-slate-500">Início</p>
              <p className="mt-0.5 font-semibold text-slate-900">{formatDate(sub.current_period_start)}</p>
            </div>
            <div className="rounded-lg bg-slate-50 px-4 py-3">
              <p className="text-xs text-slate-500">Vencimento</p>
              <p className="mt-0.5 font-semibold text-slate-900">{formatDate(sub.current_period_end)}</p>
            </div>
            <div className="rounded-lg bg-slate-50 px-4 py-3">
              <p className="text-xs text-slate-500">Dias restantes</p>
              <p className="mt-0.5 font-semibold text-slate-900">{sub.days_remaining ?? '—'}</p>
            </div>
          </div>
        </section>
      )}

      {/* No subscription */}
      {!sub && (
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-warning-600" />
            <div>
              <h2 className="text-lg font-bold text-slate-900">Nenhuma assinatura ativa</h2>
              <p className="text-sm text-slate-500">Escolha um plano abaixo para começar a usar.</p>
            </div>
          </div>
        </section>
      )}

      {/* Plan cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {(data?.plans || []).map((plan) => {
          const isCurrent = sub?.plan_code === plan.id
          const isEnterprise = plan.id === 'enterprise'
          return (
            <div
              key={plan.id}
              className={`relative flex flex-col rounded-xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${isCurrent ? 'border-primary-500 ring-2 ring-primary-200' : 'border-slate-200'}`}
            >
              {isCurrent && (
                <span className="absolute -top-2.5 right-4 rounded-full bg-primary-500 px-3 py-0.5 text-xs font-bold text-white">
                  Atual
                </span>
              )}
              <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
              <p className="mt-2 text-3xl font-bold text-slate-900 tabular-nums">
                {plan.price_formatted}
              </p>
              <p className="text-xs text-slate-500">/{plan.interval === 'month' ? 'mês' : plan.interval}</p>
              <div className="mt-6 flex-1 space-y-3">
                {Object.entries(plan.limits || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-primary-600" />
                    <span className="text-slate-700">{String(value)} {key.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={`mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold shadow-sm transition-all ${isEnterprise ? 'btn-secondary' : 'btn-primary'}`}
                disabled={checkoutLoading === plan.id || isCurrent}
                onClick={() => handleCheckout(plan.id)}
              >
                {checkoutLoading === plan.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {isCurrent ? 'Plano atual' : isEnterprise ? 'Sob consulta' : 'Assinar'}
                    {!isCurrent && !isEnterprise && <CreditCard className="h-4 w-4" />}
                  </>
                )}
              </button>
            </div>
          )
        })}
      </div>

      {/* Checkout return info */}
      {sub?.checkout_url && sub?.status === 'pending_payment' && (
        <section className="rounded-xl border border-warning-200 bg-warning-50 p-6">
          <div className="flex items-start gap-3">
            <ExternalLink className="mt-0.5 h-5 w-5 shrink-0 text-warning-700" />
            <div>
              <h3 className="font-semibold text-warning-900">Pagamento pendente</h3>
              <p className="mt-1 text-sm text-warning-800">
                Seu checkout já foi gerado. Complete o pagamento para ativar o plano.
              </p>
              <a
                href={sub.checkout_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-warning-900 underline underline-offset-2 hover:text-warning-800"
              >
                Ir para o checkout <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
